import React from 'react';
import { Button } from 'components/_new/Button';
import { InputText } from 'components/_new/InputText';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Language } from 'types/Language';
import { slugify } from 'utils/helpers';
import { Field } from 'components/_new/Field';

export type LanguageFormData = {
	language: string;
	name: string;
	slug: string;
};

type ModifyDirectoryFormProps = {
	programLanguage: Language;
	optionalLanguages: Language[];
	onSubmit: (translations: LanguageFormData[]) => void;
	currentTranslations?: LanguageFormData[];
};

export const ModifyDirectoryForm = ({
	programLanguage,
	optionalLanguages,
	currentTranslations,
	onSubmit,
}: ModifyDirectoryFormProps) => {
	const { t } = useTranslation();

	const languages = [programLanguage, ...optionalLanguages];
	const translations = languages
		.map(({ code }) => {
			const currentTranslation = currentTranslations?.find(({ language }) => language === code);
			if (currentTranslation) {
				return {
					language: code,
					name: currentTranslation.name,
					slug: currentTranslation.slug,
				};
			}
			if (!currentTranslation && code === programLanguage.code) {
				return {
					language: code,
					name: '',
					slug: '',
				};
			}
			return null;
		})
		.filter(Boolean) as LanguageFormData[];

	const { control, setValue, handleSubmit, reset } = useForm<{ translations: LanguageFormData[] }>({
		defaultValues: {
			translations,
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'translations',
	});

	const addTranslation = (languageCode: string) => {
		const translationExists = fields.findIndex(({ language }) => language === languageCode) !== -1;
		if (!translationExists && languageCode !== programLanguage.code) {
			append({ language: languageCode, name: '', slug: '' });
		}
	};

	const removeTranslation = (languageCode: string) => {
		const fieldIndex = fields.findIndex(({ language }) => language === languageCode);
		if (fieldIndex !== -1 && languageCode !== programLanguage.code) {
			remove(fieldIndex);
		}
	};

	const isProgramLanguage = (languageCode: string) => {
		return programLanguage.code === languageCode;
	};

	const availableLanguages = languages.filter(({ code }) => !fields.map(({ language }) => language).includes(code));

	const handleSubmitForm = handleSubmit(({ translations }) => {
		onSubmit(translations);
		reset({ translations: [] });
	});

	return (
		<form id="edit-directory-form" onSubmit={handleSubmitForm} className="flex flex-column gap-4">
			{fields.map((field, index) => {
				const nativeName = languages.find(({ code }) => code === field.language)?.nativeName;

				return (
					<div key={field.id} className="flex flex-column gap-2">
						<div className="flex flex-row gap-4 justify-content-between">
							<h3 className="block m-0 p-0">
								{nativeName} ({field.language})
							</h3>
							{!isProgramLanguage(field.language) ? (
								<Button
									icon="trash"
									iconOnly
									label={t('actions.remove')}
									onClick={() => removeTranslation(field.language)}
									variant="danger"
									variantSize="sm"
								/>
							) : null}
						</div>

						<Controller
							control={control}
							name={`translations.${index}.name`}
							rules={{
								required: { value: true, message: t('misc.forms.fieldIsRequired') },
								minLength: { value: 3, message: t('misc.forms.pleaseEnterMinChars', { min: 3 }) },
							}}
							render={({ field, fieldState: { error } }) => (
								<Field
									label={t('knowledgeBase.programPanel.directory.name')}
									htmlFor={`${field.name}-${index}`}
									errorMessage={error?.message}
									horizontal
								>
									<InputText
										{...field}
										id={`${field.name}-${index}`}
										onChange={(event) => {
											const { value } = event.currentTarget;
											if (value && value.length > 0) {
												const slug = slugify(value);
												setValue(`translations.${index}.slug`, slug);
											} else {
												setValue(`translations.${index}.slug`, '');
											}
											field.onChange(event);
										}}
										className="w-full"
										maxLength={100}
									/>
								</Field>
							)}
						/>

						<Controller
							control={control}
							name={`translations.${index}.slug`}
							rules={{
								required: { value: true, message: t('misc.forms.fieldIsRequired') },
								minLength: { value: 3, message: t('misc.forms.pleaseEnterMinChars', { min: 3 }) },
							}}
							render={({ field, fieldState: { error } }) => (
								<Field
									label={t('knowledgeBase.programPanel.directory.slug')}
									htmlFor={`${field.name}-${index}`}
									errorMessage={error?.message}
									horizontal
								>
									<InputText
										{...field}
										id={`${field.name}-${index}`}
										onBlur={(event) => {
											const value = slugify(event.currentTarget.value);
											field.onChange({
												...event,
												target: { ...event.target, value },
												currentTarget: { ...event.currentTarget, value },
											});
											field.onBlur();
										}}
										className="w-full"
										maxLength={100}
									/>
								</Field>
							)}
						/>
					</div>
				);
			})}

			<div className="flex flex-column gap-2">
				{availableLanguages.map(({ code, nativeName }) => (
					<Button
						key={code}
						label={t('knowledgeBase.programPanel.directoryForm.addTranslation', {
							languageName: nativeName,
						})}
						icon="plus"
						onClick={() => addTranslation(code)}
						variant="primary-outlined"
						variantSize="sm"
					/>
				))}
			</div>
		</form>
	);
};
